/**
 * useReassignTask — supervisor-driven handoff of a pending or in-flight
 * cleaning task to a different cleaner.
 *
 * Wraps the reassign-task Edge Function. Preserves the task's status /
 * started_at / cleaning_photos; only the assignee changes. RLS on
 * cleaning_photos automatically follows the new assignee (handoff, not
 * restart).
 *
 * Same AppError-shaped failure path as useStartTask / useSubmitTask so
 * the UI can render inline blocking errors (CLAUDE.md ERROR 4).
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'

interface ReassignVars {
  taskId:             string
  newAssigneeUserId:  string
  reason?:            string
}

export interface ReassignResult {
  task_id:                    string
  previous_assignee_user_id:  string | null
  new_assignee_user_id:       string
  task_status:                'pending' | 'in_progress'
}

export function useReassignTask(): UseMutationResult<ReassignResult, AppError, ReassignVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<ReassignResult, AppError, ReassignVars>({
    mutationFn: async ({ taskId, newAssigneeUserId, reason }): Promise<ReassignResult> => {
      const { data, error } = await supabase.functions.invoke<ReassignResult>('reassign-task', {
        body: {
          task_id:              taskId,
          new_assignee_user_id: newAssigneeUserId,
          reason:               reason && reason.trim().length > 0 ? reason.trim() : undefined,
        },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      if (error) {
        const ctx = error as unknown as { context?: Response }
        let code   = 'REASSIGN_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error) code = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
        })
      }
      if (!data) {
        throw new AppError({
          code: 'EMPTY_RESPONSE', message: 'No data', userMessage: 'Reassign returned no result.', status: 500,
        })
      }
      return data
    },

    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['task',     vars.taskId] })
      void queryClient.invalidateQueries({ queryKey: ['my-tasks']               })
      void queryClient.invalidateQueries({ queryKey: ['room-board']             })
      void queryClient.invalidateQueries({ queryKey: ['org-rooms']              })
      void queryClient.invalidateQueries({ queryKey: ['building-rooms']         })
    },
  })
}

function userMessageFor(code: string, detail: string): string {
  switch (code) {
    case 'FORBIDDEN_ROLE':           return 'Only supervisors and admins can reassign a task.'
    case 'FORBIDDEN_ORG':            return 'This task does not belong to your organization.'
    case 'TASK_NOT_FOUND':           return 'This task could not be found.'
    case 'INVALID_STATE':            return 'This task can no longer be reassigned (only pending or in-progress tasks can be handed off).'
    case 'NEW_ASSIGNEE_REQUIRED':    return 'Pick a cleaner to hand the task to.'
    case 'SAME_ASSIGNEE':            return 'That cleaner already owns this task.'
    case 'NEW_ASSIGNEE_NOT_ASSIGNABLE': return 'That user cannot be assigned cleaning tasks on this organization.'
    case 'INVALID_REASON':           return 'The reason must be 500 characters or fewer.'
    case 'INVALID_BODY':             return 'The request was malformed. Try again.'
    case 'RATE_LIMITED':             return 'Too many reassigns in a row. Try again in a moment.'
    default:                         return detail || 'Could not reassign the task.'
  }
}
